import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoggerService } from '@app/core/infrastructure';
import {
  PaginatedResponse,
  FeedbackDashboardItemSchema,
  FeedbackDetailSchema,
  FeedbackNoteSchema,
  FeedbackListParams,
  AddNoteRequest,
  UpdateStatusRequest,
  SendEmailRequest,
  SendEmailResponse
} from './manager-feedback-api.models';

/**
 * API service for manager feedback dashboard
 * All endpoints require manager or admin role
 */
@Injectable({
  providedIn: 'root'
})
export class ManagerFeedbackApiService {
  private readonly logger = inject(LoggerService).scope('ManagerFeedbackApiService');
  
  private readonly apiBaseUrl = `${environment.API_BASE_URL}/protected/manager/feedback`;

  constructor(
    private http: HttpClient
  ) {}

  /**
   * List feedback with pagination and filters
   * GET /v1/protected/manager/feedback
   */
  listFeedback(params?: FeedbackListParams): Observable<PaginatedResponse<FeedbackDashboardItemSchema>> {
    let httpParams = new HttpParams();

    if (params) {
      if (params.page !== undefined) {
        httpParams = httpParams.set('page', params.page.toString());
      }
      if (params.size !== undefined) {
        httpParams = httpParams.set('size', params.size.toString());
      }
      if (params.status) {
        httpParams = httpParams.set('status', params.status);
      }
      if (params.feedback_type) {
        httpParams = httpParams.set('feedback_type', params.feedback_type);
      }
      if (params.rating !== undefined) {
        httpParams = httpParams.set('rating', params.rating.toString());
      }
      if (params.order_by) {
        httpParams = httpParams.set('order_by', params.order_by);
      }
    }

    return this.http.get<PaginatedResponse<FeedbackDashboardItemSchema>>(this.apiBaseUrl, { params: httpParams }).pipe(
      catchError(error => {
        this.logger.error('Error listing feedback:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get feedback detail with notes
   * GET /v1/protected/manager/feedback/:id
   */
  getFeedbackDetail(feedbackId: string): Observable<FeedbackDetailSchema> {
    return this.http.get<FeedbackDetailSchema>(`${this.apiBaseUrl}/${feedbackId}`).pipe(
      catchError(error => {
        this.logger.error('Error getting feedback detail:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update feedback status
   * PATCH /v1/protected/manager/feedback/:id/status
   */
  updateStatus(feedbackId: string, request: UpdateStatusRequest): Observable<FeedbackDashboardItemSchema> {
    return this.http.patch<FeedbackDashboardItemSchema>(`${this.apiBaseUrl}/${feedbackId}/status`, request).pipe(
      catchError(error => {
        this.logger.error('Error updating feedback status:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Add note to feedback
   * POST /v1/protected/manager/feedback/:id/notes
   */
  addNote(feedbackId: string, request: AddNoteRequest): Observable<FeedbackNoteSchema> {
    return this.http.post<FeedbackNoteSchema>(`${this.apiBaseUrl}/${feedbackId}/notes`, request).pipe(
      catchError(error => {
        this.logger.error('Error adding note:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * List notes for feedback (paginated)
   * GET /v1/protected/manager/feedback/:id/notes
   */
  listNotes(feedbackId: string, page?: number, size?: number): Observable<PaginatedResponse<FeedbackNoteSchema>> {
    let httpParams = new HttpParams();

    if (page !== undefined) {
      httpParams = httpParams.set('page', page.toString());
    }
    if (size !== undefined) {
      httpParams = httpParams.set('size', size.toString());
    }

    return this.http.get<PaginatedResponse<FeedbackNoteSchema>>(
      `${this.apiBaseUrl}/${feedbackId}/notes`,
      { params: httpParams }
    ).pipe(
      catchError(error => {
        this.logger.error('Error listing notes:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Send email to feedback user
   * POST /v1/protected/manager/feedback/:id/send-email
   */
  sendEmail(feedbackId: string, request: SendEmailRequest): Observable<SendEmailResponse> {
    return this.http.post<SendEmailResponse>(`${this.apiBaseUrl}/${feedbackId}/send-email`, request).pipe(
      catchError(error => {
        this.logger.error('Error sending email:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a feedback entry (cascade deletes all associated notes)
   * DELETE /v1/protected/manager/feedback/:id
   */
  deleteFeedback(feedbackId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/${feedbackId}`).pipe(
      catchError(error => {
        this.logger.error('Error deleting feedback:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a single note from a feedback entry
   * DELETE /v1/protected/manager/feedback/:id/notes/:noteId
   */
  deleteNote(feedbackId: string, noteId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/${feedbackId}/notes/${noteId}`).pipe(
      catchError(error => {
        this.logger.error('Error deleting note:', error);
        return throwError(() => error);
      })
    );
  }
}
